import { useState } from 'react'
import type { ModalFile } from './useModalFiles'
import { fileIcon, fileTypeLabel } from './useModalFiles'

interface Props {
  files: ModalFile[]
  heading: string
  emptyMessage: string
  addError: string | null
  onRemove: (name: string) => void
  onDrop: (files: FileList) => void
  onOpenDialog: () => void
}

export default function ModalFileList({ files, heading, emptyMessage, addError, onRemove, onDrop, onOpenDialog }: Props) {
  const [removingFile, setRemovingFile] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-bold tracking-widest text-gray-500 uppercase">{heading}</p>
        <span className="text-xs text-gray-400">{files.length} {files.length === 1 ? 'file' : 'files'}</span>
      </div>
      <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto pr-0.5">
        {files.map((f) => {
          const { label, className } = fileTypeLabel(f.name)
          return (
            <div key={f.name} className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
              <span className="text-base shrink-0">{fileIcon(f.name)}</span>
              <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
              <span className={`px-2 py-0.5 rounded font-medium shrink-0 ${className}`}>{label}</span>
              {removingFile === f.name ? (
                <span className="flex items-center gap-1.5 shrink-0 ml-1">
                  <span className="text-gray-500">Remove?</span>
                  <button
                    onClick={() => { onRemove(f.name); setRemovingFile(null) }}
                    className="px-2 py-0.5 bg-red-500 text-white rounded font-semibold hover:bg-red-600 transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setRemovingFile(null)}
                    className="text-gray-400 hover:text-gray-600 underline"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setRemovingFile(f.name)}
                  className="ml-1 text-gray-400 hover:text-red-500 text-base leading-none shrink-0 transition-colors"
                  title="Remove file"
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              )}
            </div>
          )
        })}
        {files.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-2">{emptyMessage}</p>
        )}
      </div>
      <div
        onDrop={(e) => { e.preventDefault(); setDragging(false); onDrop(e.dataTransfer.files) }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onClick={onOpenDialog}
        className={`border-2 border-dashed rounded-xl p-3 text-center cursor-pointer transition-all mb-1 ${
          dragging ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <div className="text-lg mb-0.5">📂</div>
        <div className="text-xs font-semibold text-gray-600">Drop a file here or click to add</div>
        <div className="text-xs text-gray-400 mt-0.5">PDF · DOCX · PPTX · CSV · XLSX · JSON</div>
      </div>
      {addError ? (
        <p className="text-xs text-red-500 mt-1 mb-3">{addError}</p>
      ) : (
        <div className="mb-4" />
      )}
    </>
  )
}
